// Cloud's screen: Me at /me, put together from the same parts in the same
// order as the offline edition (apps/local/src/renderer/main.tsx), plus what
// only cloud has, through Me's extensions:
// - onUnauthorized: a lost session inside the app goes to /signin (session.ts),
// - persister: the query cache is kept in IndexedDB and drawn at once on the
//   next start; signing out removes it (clearPersistedCache),
// - the account menu (sign out; the previews for developers; the operator's
//   place /admin for the operator), the settings items of the account
//   (sign-in and security, connected apps, the previews),
// - the rail (Me and the workspaces), links between Me and the workspaces
//   (workspace/windows.ts), the workspaces at /<slug>, and a workspace's
//   sidebar around Me's pages opened from it (workspace/me-place.ts),
// - the routes outside /me (routes.tsx, workspace/routes.tsx),
// - in the mac desktop app, where its traffic lights sit (the desktop shell's
//   `desktopTitleBar`, which the offline edition's screen uses too).
// Live editing reaches the API's /collab on this origin.
//
// Me's stream runs in Me and in a workspace (its chips and favorites follow
// Me's events); the team's stream runs while the window shows a workspace.
//
// Notices: web push through the service worker (/sw.js, registered here; a
// clicked notification moves this window), the destinations below Me's
// notices settings (notices/), and this device's destination removed before
// signing out (never holding it up). The app's badge is cleared when the
// session ends.
import "./styles.css";
import { createAppUpdate } from "./app-update/app-update";
import { appUpdatePart } from "./app-update/part";
import { installStaleChunkReload } from "./app-update/stale-chunk";
import "@trameria/ui-core/documents.css";
import "@trameria/ui-core/phone.css";
import type { MeCloudResponse } from "@trameria/cloud-api/sandboxes-shared";
import { WORKSPACE_DOCUMENT_REF_PATHS } from "@trameria/cloud-api/workspace-documents-shared";
import { overwriteGetLocale as cloudLocale, m } from "@trameria/cloud-i18n";
import { desktopTitleBar } from "@trameria/desktop-shell/bridge";
import { getLocale } from "@trameria/i18n";
import {
  agentsScreen,
  casesScreen,
  clearAppBadge,
  createApi,
  createMeApp,
  createQueryClient,
  createWindowShellHost,
  documentsScreen,
  hostAppSettings,
  inboxScreen,
  indexedDbPersister,
  initLocale,
  labelsPart,
  loadScreensFor,
  loadUiFenceDocumentBlocks,
  type MeAppInstance,
  type MenuItem,
  navigateFromNotice,
  personsScreen,
  publishPart,
  timeBlockerScreen,
  useDelegateStepMenu,
} from "@trameria/ui-core";
import { phoneDockSettings, phoneKit } from "@trameria/ui-core/phone";
import { createRoot } from "react-dom/client";
import { adminRoutes } from "./admin/admin";
import { CloudContext } from "./context";
import { conversationsScreen } from "./conversations/part";
import {
  desktopFrame,
  desktopFramePart,
  frameLayout,
  frameShellHost,
  frameTitleBar,
  tellSignedOut,
} from "./desktop-frame";
import { meHomePart, webMeAway } from "./me-home/me-home";
import {
  cloudNoticesPart,
  cloudNoticesScreen,
  forgetDeviceBeforeSignOut,
  listenServiceWorkerNavigate,
  registerServiceWorker,
} from "./notices";
import { personLink } from "./person-link";
import { lastPageToRestore, saveLastPage } from "./pwa/last-page";
import { type Account, accountKey, accountQuery } from "./queries";
import { cloudRoutes, currentAccount, requireApproved } from "./routes";
import { cloudAdminItem } from "./sandboxes/admin";
import { useSandboxDecor } from "./sandboxes/decor";
import { sandboxesPart } from "./sandboxes/part";
import { previewsSettings } from "./sandboxes/previews";
import { meCloudKey, meCloudQuery } from "./sandboxes/queries";
import { sandboxRoutes } from "./sandboxes/routes";
import { waitlistAdminItem } from "./screens/admin-waitlist";
import { createCloudSession, inApp } from "./session";
import { appsSettings } from "./settings/apps";
import { HomeIconRow, homeIconSections } from "./settings/home-icon";
import { securitySettings } from "./settings/security";
import { callsScreen } from "./voice/part";
import { mePlaceOf } from "./workspace/me-place";
import { workspaceScreen } from "./workspace/part";
import { keepPlacePage } from "./workspace/place-pages";
import { inWorkspacePath } from "./workspace/places";
import { createTeamStream } from "./workspace/realtime";
import { createWindows, navigateRouter } from "./workspace/windows";
import {
  workspaceDocumentsBase,
  workspaceDocumentsScreen,
} from "./workspace-documents/part";

declare const __BUILD_ID__: string;

initLocale();
cloudLocale(() => getLocale());

const bootSearch = window.location.search;
const queryClient = createQueryClient();
const persister = indexedDbPersister();
const scheme = location.protocol === "https:" ? "wss" : "ws";
const live = { url: `${scheme}://${location.host}/collab` };

let me: MeAppInstance | null = null;
// Inside the desktop app's screen its session is the app's to make again
// (desktop-frame.tsx); elsewhere a lost session goes to sign-in.
const lostSession = () =>
  desktopFrame ? tellSignedOut() : session.onUnauthorized();
const instance = () => {
  if (!me) throw new Error("the Me app is not made yet");
  return me;
};
// The same API Me uses; its 401 goes to the session.
const api = createApi({ onUnauthorized: () => lostSession() });

const windows = createWindows({
  navigate: navigateRouter(() => instance().router),
});
const conversations = conversationsScreen();
// Issues are held back from people for now (the person, 2026-10-18): their
// section, Me part and the ui fences' createIssue stay out until they come back.
const documents = workspaceDocumentsScreen({
  live,
  blocks: loadUiFenceDocumentBlocks,
});
const calls = callsScreen();
// A window left open learns that a newer screen is out (app-update/).
const appUpdate = createAppUpdate({
  loaded: __BUILD_ID__,
  canReloadSilently: () => calls.session.get().status === "idle",
});
appUpdate.install();
installStaleChunkReload();
const workspace = workspaceScreen({
  live,
  windows,
  sections: [...conversations.sections, documents.section, calls.section],
});
const team = createTeamStream({
  qc: queryClient,
  api,
  handlers: workspace.teamHandlers,
});

// Inside a native app's web view (iOS, Android): the app draws the menu
// and the headers from data. In a browser there is none.
// A native app's web view, or the desktop app's frame (desktop-frame.tsx).
const shellHost = createWindowShellHost(window) ?? frameShellHost ?? undefined;
const session = createCloudSession({
  api,
  queryClient,
  persister,
  startMe: () => instance().start(),
  router: () => instance().router,
  beforeSignOut: () => forgetDeviceBeforeSignOut(api),
  onEnd: () => {
    team.stop();
    clearAppBadge();
  },
});

me = createMeApp({
  // In the app's frame, moves stay out of the window's history: the app keeps
  // it (its back and forward move this page too), so the two never disagree.
  ...(desktopFrame
    ? {
        initialUrl: `${location.pathname}${location.search}${location.hash}`,
      }
    : {}),
  parts: [
    // What waits for the person (the badge's things: agent chats and the
    // workspaces' direct messages and mentions) and the settled items.
    inboxScreen,
    // A step's "…" gets "Hand off to Agent" from the Agent chat part.
    casesScreen({ live, stepMenu: useDelegateStepMenu }),
    timeBlockerScreen,
    // People can be linked to members of the person's workspaces.
    personsScreen(personLink),
    // The browser has nothing to do with devices: no settings item for them
    // (a device's agent settings are in the app's device list).
    { ...agentsScreen, settings: [] },
    // A shared document opens in its workspace (favorites, chips, its path),
    // and a pasted URL of one becomes a reference.
    documentsScreen({
      live,
      placeBase: workspaceDocumentsBase,
      refPaths: WORKSPACE_DOCUMENT_REF_PATHS,
      // ```ui blocks in a document run (loaded only when a document has one).
      blocks: loadUiFenceDocumentBlocks,
    }),
    // Publishing a document by link: a mark and an item in its header.
    publishPart,
    labelsPart,
    workspace.part,
    conversations.mePart,
    // The workspaces' lists of documents hear Me's events; "Move to a workspace".
    documents.mePart,
    cloudNoticesScreen,
    cloudNoticesPart,
    sandboxesPart,
    // The call I am in: its sound outside every screen, its row in every sidebar.
    calls.mePart,
    appUpdatePart(appUpdate),
    // Me living on the person's Hub: Me's pages say so (me-home/).
    meHomePart,
    // Inside the desktop app's screen: what the app hears from this one.
    ...(desktopFrame ? [desktopFramePart] : []),
  ],
  queryClient,
  buildId: __BUILD_ID__,
  extensions: {
    onUnauthorized: () => lostSession(),
    meAway: webMeAway,
    // Personal lists show here only while the data is the cloud's (else away).
    personalPlace: "cloud",
    persister,
    // Inside the desktop app's screen its rail is the app's (desktop-frame.tsx).
    ...(desktopFrame
      ? { frameLayout }
      : { Rail: workspace.Rail, useRail: workspace.useRail }),
    useSidebar: workspace.useSidebar,
    openTarget: workspace.openTarget,
    // On a phone: the Dock (Me's places and the workspace's) for the drawer.
    phone: phoneKit(),
    dock: workspace.dock,
    shellHost,
    // The previews show only to developers and operators.
    settings: [
      securitySettings,
      appsSettings,
      phoneDockSettings,
      previewsSettings,
    ],
    // In the desktop app whose own settings are a window (Settings…), this
    // app's items are there, and one row here opens it. The same in the iOS
    // app, whose own settings are its own screen.
    ...(window.trameriaDesktop?.appSettings
      ? {
          appSettings: {
            open: () => window.trameriaDesktop?.appSettings?.open(),
          },
        }
      : hostAppSettings(shellHost)
        ? { appSettings: hostAppSettings(shellHost) }
        : {}),
    // The home screen icon's colors, in Appearance's box (not in the desktop
    // app: it is never added to a home screen).
    appearanceRows: window.trameriaDesktop
      ? undefined
      : { Rows: HomeIconRow, sections: homeIconSections },
    // The account's name and picture are this server's own profile (what the
    // workspaces show where the person set none of their own).
    account: "profile",
    settingsTitle: () => m.web_settings_title(),
    settingsDescription: () => m.web_settings_description(),
    // In the app's frame, the app's window controls over it (the app says).
    titleBar: desktopFrame
      ? frameTitleBar
      : desktopTitleBar(window.trameriaDesktop),
    // The person's trameria cloud among their agent devices (sandboxes/).
    deviceDecor: { useDecor: useSandboxDecor },
    accountMenu: () => {
      const items: MenuItem[] = [];
      const go = (to: string) => () => void instance().router.navigate({ to });
      // Read from the cache, kept fresh by `enterApp` below.
      const admin =
        queryClient.getQueryData<Account>(accountKey)?.role === "admin";
      // A way straight to the settings' item (developers open it often).
      if (
        admin ||
        queryClient.getQueryData<MeCloudResponse>(meCloudKey)?.dev.allowed
      )
        items.push({
          id: "previews",
          label: m.sandboxes_menu_previews(),
          icon: "eye",
          onSelect: go("/me/settings/previews"),
        });
      // The operator's place: its sidebar has every operator's page.
      if (admin)
        items.push({
          id: "admin",
          label: m.web_menu_admin(),
          icon: "sliders",
          onSelect: go("/admin"),
        });
      // Inside the desktop app the session is the app's (it makes it again):
      // signing out is in the app's settings (Cloud).
      if (!desktopFrame)
        items.push({
          id: "sign-out",
          label: m.web_menu_sign_out(),
          icon: "signOut",
          onSelect: () => void session.signOut(),
        });
      return items;
    },
    // The workspaces' screens load ahead while idle, as Me's do (not their
    // settings, the sign-in pages or the operator's place).
    warmRoutes: (path) =>
      path.startsWith("/$slug/") && !path.startsWith("/$slug/settings"),
    routes: (root) => [
      ...cloudRoutes(root, session),
      ...sandboxRoutes(root, session),
      ...adminRoutes(root, session, [waitlistAdminItem, cloudAdminItem]),
      ...workspace.routes(root, {
        requireApproved: (ctx, back) => requireApproved(ctx, session, back),
        primeAccount: (ctx) => currentAccount(ctx, session),
      }),
    ],
  },
});

registerServiceWorker();
listenServiceWorkerNavigate((url) =>
  navigateFromNotice(instance().router, url),
);

// Me's stream and kept cache run while the person is in the app.
const enterApp = async () => {
  await session.ensure();
  void queryClient.prefetchQuery(accountQuery(api));
  // The account menu's "Previews" reads whether a development sandbox is allowed.
  void queryClient.prefetchQuery(meCloudQuery(api));
};
me.router.subscribe("onResolved", ({ toLocation }) => {
  const path = toLocation.pathname;
  saveLastPage(toLocation.href, inApp(path));
  // Me's page shown in a workspace is neither Me's page to go back to nor
  // the workspace's (it opens Me's pages only from its own rows).
  if (mePlaceOf(path) === null) keepPlacePage(path);
  if (inApp(path)) void enterApp();
  // A workspace's topic beside an Agent chat in Me hears the team's events too.
  if (inWorkspacePath(path) || conversations.wantsTeamStream(path))
    team.ensure();
  else team.stop();
});

const root = document.getElementById("app");
if (!root) throw new Error("no #app element");
// The home screen app started at / goes back to the page it was left on.
const lastPage = lastPageToRestore(window.location.pathname, inApp);
if (lastPage) me.router.history.replace(lastPage);
// The opened screen's code loads while the session is checked.
loadScreensFor(me.router, me.router.history.location.pathname);
// The kept cache goes back before the first paint (/ leads to /me).
const path = window.location.pathname;
if (path === "/" || inApp(path)) await enterApp();
const Root = me.Root;
createRoot(root).render(
  <CloudContext.Provider value={{ session, bootSearch }}>
    <Root />
  </CloudContext.Provider>,
);
